import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CircleNotch, Buildings, ArrowRight, BookOpen } from "@phosphor-icons/react";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1691457782561-2a5c8044e144?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMG5pZ2h0fGVufDB8fHx8MTc4NjgzOTYwNnww&ixlib=rb-4.1.0&q=85";

function Field({ label, children, htmlFor }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-widest text-zinc-500">
        {label}
      </Label>
      {children}
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [setupForm, setSetupForm] = useState({
    razon_social: "",
    nombre_comercial: "",
    rtn: "",
    direccion: "",
    correo: "",
    telefono: "",
    admin_full_name: "",
    admin_username: "",
    admin_password: "",
  });
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotForm, setForgotForm] = useState({ email: "", code: "", new_password: "" });

  const inputCls =
    "rounded-sm border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";

  const doLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(loginForm.username.trim(), loginForm.password);
      toast.success("Sesión iniciada");
      navigate("/");
    } catch (err) {
      setError(apiError(err, "No se pudo iniciar sesión."));
    } finally {
      setLoading(false);
    }
  };

  const doSetup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/company/setup", setupForm);
      toast.success("Empresa creada. Ahora inicia sesión con tu usuario administrador.");
      setLoginForm({ username: setupForm.admin_username, password: "" });
      setTab("login");
    } catch (err) {
      setError(apiError(err, "No se pudo crear la empresa."));
    } finally {
      setLoading(false);
    }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (forgotStep === 1) {
        await api.post("/auth/forgot-password", { email: forgotForm.email });
        toast.success("Si el correo existe, recibirás un código de 6 dígitos.");
        setForgotStep(2);
      } else {
        await api.post("/auth/reset-password", forgotForm);
        toast.success("Contraseña actualizada. Inicia sesión.");
        setForgotOpen(false);
        setForgotStep(1);
        setForgotForm({ email: "", code: "", new_password: "" });
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-background" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div className="font-heading text-2xl font-black tracking-tight text-white">
            CAUS<span className="text-zinc-500">·</span>RRHH
          </div>
          <div>
            <h2 className="font-heading text-4xl font-bold tracking-tight text-white leading-tight">
              El portal web de tu
              <br />
              suite de recursos humanos
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
              Conectado en tiempo real con la app móvil CAUS - HR Suite. Gestiona empleados, turnos, horarios,
              asistencia, planillas y documentos desde un solo lugar.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 border border-zinc-700 px-3 py-1.5 text-xs uppercase tracking-widest text-zinc-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Enlace activo con CAUS-RRHH
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden font-heading text-2xl font-black tracking-tight text-foreground">
            CAUS<span className="text-zinc-500">·</span>RRHH
          </div>
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
            <TabsList className="grid w-full grid-cols-2 rounded-sm bg-muted border border-border">
              <TabsTrigger value="login" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-login">
                Iniciar sesión
              </TabsTrigger>
              <TabsTrigger value="setup" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-setup">
                Nueva empresa
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-8">
              <form onSubmit={doLogin} className="space-y-5" data-testid="login-form">
                <Field label="Usuario o correo" htmlFor="login-username">
                  <Input
                    id="login-username"
                    data-testid="login-username-input"
                    className={inputCls}
                    value={loginForm.username}
                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                    placeholder="usuario o correo@empresa.com"
                    required
                    autoComplete="username"
                  />
                </Field>
                <Field label="Contraseña" htmlFor="login-password">
                  <Input
                    id="login-password"
                    type="password"
                    data-testid="login-password-input"
                    className={inputCls}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                </Field>
                {error && (
                  <p className="border border-red-900/60 bg-red-950/30 px-3 py-2 text-sm text-red-400" data-testid="login-error">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/80 font-medium"
                  data-testid="login-submit-button"
                >
                  {loading ? <CircleNotch className="animate-spin" size={18} /> : <ArrowRight size={18} />}
                  <span className="ml-2">Entrar al portal</span>
                </Button>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </form>
            </TabsContent>

            <TabsContent value="setup" className="mt-8">
              <form onSubmit={doSetup} className="space-y-4" data-testid="setup-form">
                <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
                  <Buildings size={16} /> Registra tu empresa en CAUS-RRHH
                </div>
                <Field label="Razón social *" htmlFor="setup-razon">
                  <Input id="setup-razon" data-testid="setup-razon-input" className={inputCls} required value={setupForm.razon_social}
                    onChange={(e) => setSetupForm({ ...setupForm, razon_social: e.target.value })} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Nombre comercial" htmlFor="setup-nombre">
                    <Input id="setup-nombre" className={inputCls} value={setupForm.nombre_comercial}
                      onChange={(e) => setSetupForm({ ...setupForm, nombre_comercial: e.target.value })} />
                  </Field>
                  <Field label="RTN" htmlFor="setup-rtn">
                    <Input id="setup-rtn" data-testid="setup-rtn-input" className={inputCls} value={setupForm.rtn}
                      onChange={(e) => setSetupForm({ ...setupForm, rtn: e.target.value })} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Correo" htmlFor="setup-correo">
                    <Input id="setup-correo" type="email" className={inputCls} value={setupForm.correo}
                      onChange={(e) => setSetupForm({ ...setupForm, correo: e.target.value })} />
                  </Field>
                  <Field label="Teléfono" htmlFor="setup-tel">
                    <Input id="setup-tel" className={inputCls} value={setupForm.telefono}
                      onChange={(e) => setSetupForm({ ...setupForm, telefono: e.target.value })} />
                  </Field>
                </div>
                <div className="border-t border-border pt-4 mt-2 space-y-4">
                  <p className="text-xs uppercase tracking-widest text-zinc-500">Administrador</p>
                  <Field label="Nombre completo" htmlFor="setup-admin-name">
                    <Input id="setup-admin-name" className={inputCls} value={setupForm.admin_full_name}
                      onChange={(e) => setSetupForm({ ...setupForm, admin_full_name: e.target.value })} />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Usuario *" htmlFor="setup-admin-user">
                      <Input id="setup-admin-user" data-testid="setup-username-input" className={inputCls} required value={setupForm.admin_username}
                        onChange={(e) => setSetupForm({ ...setupForm, admin_username: e.target.value })} />
                    </Field>
                    <Field label="Contraseña *" htmlFor="setup-admin-pass">
                      <Input id="setup-admin-pass" type="password" data-testid="setup-password-input" className={inputCls} required value={setupForm.admin_password}
                        onChange={(e) => setSetupForm({ ...setupForm, admin_password: e.target.value })} />
                    </Field>
                  </div>
                </div>
                {error && (
                  <p className="border border-red-900/60 bg-red-950/30 px-3 py-2 text-sm text-red-400" data-testid="setup-error">
                    {error}
                  </p>
                )}
                <Button type="submit" disabled={loading} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/80 font-medium" data-testid="setup-submit-button">
                  {loading && <CircleNotch className="animate-spin mr-2" size={16} />}
                  Crear empresa
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => navigate("/manual")}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors duration-200"
              data-testid="manual-link"
            >
              <BookOpen size={14} /> ¿Primera vez aquí? Consulta el manual de uso
            </button>
          </div>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-card border-border rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Recuperar contraseña</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {forgotStep === 1
                ? "Te enviaremos un código de 6 dígitos a tu correo."
                : "Ingresa el código recibido y tu nueva contraseña."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={doForgot} className="space-y-4" data-testid="forgot-form">
            {forgotStep === 1 ? (
              <Field label="Correo electrónico" htmlFor="forgot-email">
                <Input id="forgot-email" type="email" data-testid="forgot-email-input" className={inputCls} required
                  value={forgotForm.email} onChange={(e) => setForgotForm({ ...forgotForm, email: e.target.value })} />
              </Field>
            ) : (
              <>
                <Field label="Código de verificación" htmlFor="forgot-code">
                  <Input id="forgot-code" data-testid="forgot-code-input" className={inputCls} required maxLength={6}
                    value={forgotForm.code} onChange={(e) => setForgotForm({ ...forgotForm, code: e.target.value })} />
                </Field>
                <Field label="Nueva contraseña" htmlFor="forgot-new">
                  <Input id="forgot-new" type="password" data-testid="forgot-newpass-input" className={inputCls} required
                    value={forgotForm.new_password} onChange={(e) => setForgotForm({ ...forgotForm, new_password: e.target.value })} />
                </Field>
              </>
            )}
            <Button type="submit" disabled={loading} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="forgot-submit-button">
              {loading && <CircleNotch className="animate-spin mr-2" size={16} />}
              {forgotStep === 1 ? "Enviar código" : "Restablecer contraseña"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
